import { NovelGalleryThunkAction } from "@customer-gallery/redux/galleryTypedRedux";
import { ICryptoAddress } from "@novel/shared/interfaces/CryptoAddress";
import { ICryptoWallet } from "@novel/shared/interfaces/CryptoWallet";
import { ITokenCollectionClaimWithTokenCollection } from "@novel/shared/interfaces/TokenCollectionClaim";
import { retryWithBackoff } from "@novel/shared/utils/retryWithBackoff";
import { normalizeBlockchainError } from "@novel/shared/utils/normalizeBlockchainError";
import { isErrorSurfaceable } from "@novel/shared/utils/SurfaceableError";
import { dismissToast, loadingToast } from "@novel/shared/utils/toastUtils";
import { mintTokensForClaimCentralizedApiCall } from "@customer-gallery/services/utils/mintTokensForClaimApiCall";
import { GalleryMintingWallet } from "@customer-gallery/utils/galleryWallets";
import { typedFrontendGalleryApi } from "@customer-gallery/utils/typedFrontendGalleryApi";

export const mintTokensToWhitelistedWallet =
    (
        inputCryptoAddress: ICryptoAddress,
        wallet: ICryptoWallet,
        tokenClaim: ITokenCollectionClaimWithTokenCollection,
    ): NovelGalleryThunkAction<
        | "LOADING_MINTING_TOKENS_TO_WALLET"
        | "ERROR_MINTING_TOKENS_TO_WALLET"
        | "LOADED_MINTING_TOKENS_TO_WALLET"
    > =>
    async (dispatch) => {
        const { claimOrder } = tokenClaim;
        const { collectionMetadata } = tokenClaim.collectionToMintFrom;
        const collectionContract = collectionMetadata.collectionSmartContract!;

        const { onSuccessToast } = loadingToast(
            `Minting ${tokenClaim.quantity} token${tokenClaim.quantity > 1 ? "s" : ""}${
                collectionMetadata.decentralizedMintEnabled ? " (this can take a minute)" : ""
            }...`,
        );

        await dispatch({
            type: "LOADING_MINTING_TOKENS_TO_WALLET",
            payload: undefined,
        });

        const showCongratsToast = () =>
            onSuccessToast(
                `Congrats! You have successfully minted ${tokenClaim.quantity} NFT${
                    tokenClaim.quantity > 1 ? "s" : ""
                }!`,
            );

        if (!collectionMetadata.decentralizedMintEnabled) {
            const dispatchRes = dispatch({
                type: "LOADED_MINTING_TOKENS_TO_WALLET",
                payload: await mintTokensForClaimCentralizedApiCall({
                    orderId: claimOrder.id,
                    claimQuantity: tokenClaim.quantity,
                    cryptoAddress: inputCryptoAddress.address,
                    collectionContractAddress: collectionContract.address,
                }),
            });

            showCongratsToast();
            return dispatchRes;
        }

        try {
            let transactionHash: string | undefined;
            if (
                !(await new GalleryMintingWallet(wallet).isClaimIdAlreadyMinted(
                    inputCryptoAddress,
                    collectionContract,
                    tokenClaim.id,
                ))
            ) {
                const result = await new GalleryMintingWallet(wallet).mintToken(
                    inputCryptoAddress,
                    collectionContract,
                    tokenClaim.quantity,
                    tokenClaim.id,
                    (transactionHash) =>
                        // schedule mint sync job as soon as we have transaction hash
                        // in case user closes the browser before the transaction is mined
                        typedFrontendGalleryApi.postReq(
                            "/api/customer/:cryptoAddress/schedule-sync-mint-transaction",
                            {
                                params: {
                                    cryptoAddress: inputCryptoAddress.address,
                                },
                                reqBody: {
                                    transactionHash,
                                    collectionContractAddress: collectionContract.address,
                                    orderId: tokenClaim.claimOrder.id,
                                },
                            },
                        ),
                );
                transactionHash = result.transactionHash;
            }

            const response = await retryWithBackoff(() =>
                typedFrontendGalleryApi.postReq(
                    "/api/customer/:cryptoAddress/sync-mint-transaction",
                    {
                        params: {
                            cryptoAddress: inputCryptoAddress.address,
                        },
                        reqBody: {
                            transactionHash,
                            collectionContractAddress: collectionContract.address,
                            orderId: tokenClaim.claimOrder.id,
                        },
                    },
                ),
            );

            if (response.type === "error") {
                throw response.error;
            }

            showCongratsToast();
            return dispatch({
                type: "LOADED_MINTING_TOKENS_TO_WALLET",
                payload: response.body,
            });
        } catch (error: any) {
            dismissToast();
            if (isErrorSurfaceable(error)) {
                dispatch({
                    type: "ERROR_MINTING_TOKENS_TO_WALLET",
                    payload: {
                        errorMessage: error.message,
                    },
                });

                throw error;
            }

            const { fullErrorMessage } = normalizeBlockchainError(error);
            dispatch({
                type: "ERROR_MINTING_TOKENS_TO_WALLET",
                payload: {
                    errorMessage: fullErrorMessage,
                },
            });

            throw new Error(`Error minting tokens to your address: ${fullErrorMessage}`);
        }
    };
